/**
 * usePopupState Hook
 * Manages event popup visibility, position, and focus trapping
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { PopupState, PopupPlacement, CalendarEvent, DayObject } from '../../../contracts/YearlyCalendarContracts';
import { calculatePopupPosition } from '../utils/gridUtils';
import { createFocusTrap, announceToScreenReader, generatePopupAriaLabel } from '../utils/accessibilityUtils';
import { format } from 'date-fns';

export interface UsePopupStateOptions {
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  trapFocus?: boolean;
  onPopupOpen?: (date: Date, events: CalendarEvent[]) => void;
  onPopupClose?: () => void;
}

export interface UsePopupStateReturn {
  // State
  popupState: PopupState;
  isOpen: boolean;
  
  // Actions
  openPopup: (day: DayObject, events: CalendarEvent[], rowIndex?: number, colIndex?: number) => void;
  closePopup: () => void;
  togglePopup: (day: DayObject, events: CalendarEvent[], rowIndex?: number, colIndex?: number) => void;
  
  // Event handlers
  handlePopupKeyDown: (e: React.KeyboardEvent) => void;
  handleOutsideClick: (e: MouseEvent) => void;
  
  // Refs
  popupRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLElement>;
}

/**
 * Custom hook for managing popup state
 */
export const usePopupState = (options: UsePopupStateOptions = {}): UsePopupStateReturn => {
  const {
    closeOnEscape = true,
    closeOnOutsideClick = true,
    trapFocus = true,
    onPopupOpen,
    onPopupClose,
  } = options;
  
  // State
  const [popupState, setPopupState] = useState<PopupState>({
    isOpen: false,
    targetDate: null,
    targetElement: null,
    events: [],
    position: 'bottom',
    rowIndex: 0,
    colIndex: 0,
  });
  
  // Refs
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const focusTrapCleanup = useRef<(() => void) | null>(null);
  const previousActiveElement = useRef<Element | null>(null);
  
  // Computed
  const isOpen = popupState.isOpen;
  
  // Open popup
  const openPopup = useCallback((
    day: DayObject,
    events: CalendarEvent[],
    rowIndex: number = 0,
    colIndex: number = 0
  ) => {
    // Store the currently focused element
    previousActiveElement.current = document.activeElement;
    
    // Calculate position
    const position = calculatePopupPosition(rowIndex, colIndex);
    
    setPopupState({
      isOpen: true,
      targetDate: day.date,
      targetElement: null,
      events,
      position,
      rowIndex,
      colIndex,
    });
    
    // Announce to screen readers
    announceToScreenReader(generatePopupAriaLabel(day.date, events.length));
    
    // Notify callback
    onPopupOpen?.(day.date, events);
  }, [onPopupOpen]);
  
  // Close popup
  const closePopup = useCallback(() => {
    // Clean up focus trap
    if (focusTrapCleanup.current) {
      focusTrapCleanup.current();
      focusTrapCleanup.current = null;
    }
    
    setPopupState(prev => ({
      ...prev,
      isOpen: false,
    }));
    
    // Restore focus to trigger element
    if (previousActiveElement.current instanceof HTMLElement) {
      previousActiveElement.current.focus();
    }
    
    // Notify callback
    onPopupClose?.();
    
    announceToScreenReader('Popup closed');
  }, [onPopupClose]);
  
  // Toggle popup
  const togglePopup = useCallback((
    day: DayObject,
    events: CalendarEvent[],
    rowIndex: number = 0,
    colIndex: number = 0
  ) => {
    if (isOpen && popupState.targetDate && day.date.getTime() === popupState.targetDate.getTime()) {
      closePopup();
    } else {
      openPopup(day, events, rowIndex, colIndex);
    }
  }, [isOpen, popupState.targetDate, openPopup, closePopup]);
  
  // Handle keyboard events in popup
  const handlePopupKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (closeOnEscape && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closePopup();
    }
  }, [closeOnEscape, closePopup]);
  
  // Handle outside click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (!closeOnOutsideClick) return;
    
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      // Check if click is on the trigger
      if (triggerRef.current && triggerRef.current.contains(e.target as Node)) {
        return;
      }
      closePopup();
    }
  }, [closeOnOutsideClick, closePopup]);
  
  // Setup focus trap when popup opens
  useEffect(() => {
    if (isOpen && trapFocus && popupRef.current) {
      // Set up focus trap
      focusTrapCleanup.current = createFocusTrap(popupRef.current);
      
      // Focus first focusable element
      const focusable = popupRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        focusable.focus();
      }
    }
    
    return () => {
      if (focusTrapCleanup.current) {
        focusTrapCleanup.current();
        focusTrapCleanup.current = null;
      }
    };
  }, [isOpen, trapFocus]);
  
  // Setup outside click listener
  useEffect(() => {
    if (isOpen && closeOnOutsideClick) {
      // Delay adding listener to avoid immediate trigger
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleOutsideClick);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [isOpen, closeOnOutsideClick, handleOutsideClick]);
  
  // Setup escape key listener
  useEffect(() => {
    if (isOpen && closeOnEscape) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closePopup();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, closeOnEscape, closePopup]);
  
  return {
    popupState,
    isOpen,
    openPopup,
    closePopup,
    togglePopup,
    handlePopupKeyDown,
    handleOutsideClick,
    popupRef,
    triggerRef,
  };
};

export default usePopupState;
